import { Cookie, Mode, QueryParam } from "~/types";
import { config } from "~/data/config";
import { getCookie, setCookie } from "~/utils/client_cookie";
import { visit } from "unist-util-visit";
import { Product } from "~/server/types/search";
import { showSnackBar } from "~/shared_states/snackbar";
import { SearchParams } from "@solidjs/router/dist/types";
import { captureErrorInSentry } from "./third_party/sentry";

export const getModeFromCookie = (): Mode | undefined => {
  return getCookie(Cookie.Mode) as Mode | undefined;
};

export const getClientIdFromCookie = (): string | undefined => {
  return getCookie(Cookie.ClientId) as string | undefined;
};

export const getModeFromQueryParamsAndCookies = (
  queryParams?: Partial<SearchParams>
): Mode => {
  let clientId: string | undefined | null;
  let clientSecret: string | undefined | null;
  let token: string | undefined | null;

  if (getModeFromCookie() === "rnr" || getModeFromCookie() === "squid") {
    return getModeFromCookie()!;
  }

  if (!queryParams) {
    const searchParams = new URLSearchParams(window.location.search);
    clientId = searchParams.get(QueryParam.ClientId);
    clientSecret = searchParams.get(QueryParam.ClientSecret);
    token = searchParams.get(QueryParam.Token);
  } else {
    clientId = queryParams.clientId as string | undefined;
    clientSecret = queryParams.clientSecret as string | undefined;
    token = queryParams.token as string | undefined;
  }

  if (clientId && clientSecret && token) {
    return "sdk";
  }

  return "hubble";
};

export const getCategorySlugFromCategoryName = (
  categoryName: string
): string => {
  return categoryName.toLowerCase().replaceAll("_", "-") + "-gift-cards";
};

export const getBrandKeyFromSlug = (slug: string): string => {
  return slug
    .replace(/-gift-card(-.*$)?/, "")
    .replace("amazon-shopping", "amazon");
};

export const newRequestId = () => {
  return Math.random().toString(36).slice(2);
};

export const showReloadOnError = (clientId: string): boolean => {
  if (clientId.startsWith(config.clientIdPrefix.visitHealth)) {
    return true;
  }

  return false;
};

export const areMixpanelEventsEnabledFor = (mode?: Mode): boolean => {
  if (mode === "rnr") {
    return config.mixpanelEvents.enabledForRnr;
  } else if (mode === "sdk") {
    return config.mixpanelEvents.enabledForSdk;
  }
  return config.mixpanelEvents.enabledForWebStore;
};

export function hasNonNullOrEmptyValue<T extends Record<string, any>>(
  obj: T
): boolean {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (obj[key] !== null && obj[key] !== "") {
        return true;
      }
    }
  }
  return false;
}

export const convertUrlsToLinks = (text: string) => {
  const regex =
    /(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z0-9]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?\/?[a-zA-Z0-9/?=&\-._~:#[\]@!$&'()*+,;=]*(?<![.,])(?<!\))(?<!\.\))/g;

  return text.replaceAll(regex, (url: string) => {
    const href = url.startsWith("http") ? url : `https://${url}`;
    return `<a href=${href} rel="noopener noreferrer">${url}</a>`;
  });
};

export function rehypeUppercaseATags() {
  // @ts-ignore
  return (tree) => {
    visit(tree, "element", (node) => {
      if (node.tagName === "a") {
        node.tagName = "A";
      }
    });
  };
}

export const getCategoryKeyFromSlug = (slug: string): string => {
  return slug
    .replace(/-gift-cards?$/, "")
    .replaceAll("-", "_")
    .toUpperCase();
};

export const getCategoryTitleFromSlug = (slug: string): string => {
  return slug
    .replace(/-gift-cards?$/, "")
    .replaceAll("-", " ")
    .split(" ")
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

export const getCategoryNameFromSlug = (text: string): string => {
  const prefix = "gift-card-for-";
  if (text.startsWith(prefix)) {
    return text.slice(prefix.length);
  }
  return text;
};

export const getHighestPercentOff = (list: Product[]): number => {
  return (list ?? [])
    .filter(
      (product) =>
        product.voucherProduct && product.voucherProduct.discountPercentage
    )
    .reduce((max, product) => {
      return Math.max(max, product.voucherProduct.discountPercentage);
    }, 0);
};

export const formatGroup = (occasion: string) => {
  return occasion.toLowerCase().replace(/ /g, "-").replace(/'/g, "");
};

export async function generateDeviceVerificationToken(): Promise<
  string | undefined
> {
  const token = getCookie(Cookie.FpjsRequestId);
  console.log(
    "[FPJS] Existing token from cookie:",
    token ? "exists" : "not found"
  );

  if (token) {
    console.log("[FPJS] Returning existing token from cookie");
    return token;
  }

  console.log(
    "[FPJS] No existing token, generating new one via FingerPrint.run()"
  );

  try {
    if (typeof window === "undefined") {
      console.error("[FPJS] Window is undefined - running on server side");
    }

    if (!(window as any).FingerPrint) {
      console.error("[FPJS] FingerPrint is not available on window object");
    }

    // @ts-ignore
    const fpResult = await (window as any).FingerPrint.run();
    console.log("[FPJS] FingerPrint.run() result:", JSON.stringify(fpResult));

    if (!fpResult) {
      console.error("[FPJS] FingerPrint.run() returned null/undefined");
      return undefined;
    }

    const { requestId } = fpResult;

    if (!requestId) {
      console.error(
        "[FPJS] requestId is undefined/null from FingerPrint.run()"
      );
    } else {
      console.log("[FPJS] Setting cookie with requestId");
      setCookie({
        key: Cookie.FpjsRequestId,
        value: requestId,
        expiryInMinutes: 30,
      });

      console.log("[FPJS] Successfully generated and cached new token");
    }

    return requestId;
  } catch (error: any) {
    console.error("[FPJS] Error in generateDeviceVerificationToken:", error);
    console.error("[FPJS] Error details:", {
      message: error?.message,
      stack: error?.stack,
      name: error?.name,
    });

    captureErrorInSentry(error);
    return undefined;
  }
}

export const pluralize = (props: { name: string; count: number }): string => {
  return props.count === 1 ? props.name : props.name + "s";
};

export function removeWhitespace(input: string): string {
  return input.replace(/\s/g, "");
}
